import React from 'react';
import styled from 'styled-components';

interface ContentBreakProps {
  variant?: 'dots' | 'line' | 'wave' | 'gradient';
  spacing?: 'small' | 'medium' | 'large';
}

const BreakContainer = styled.div<{ $spacing: string }>`
  display: flex;
  align-items: center;
  justify-content: center;
  margin: ${props => {
    switch(props.$spacing) {
      case 'small': return 'var(--spacing-xl) 0';
      case 'large': return 'var(--spacing-4xl) 0';
      default: return 'var(--spacing-3xl) 0';
    }
  }};
  position: relative;
`;

const DotsBreak = styled.div`
  display: flex;
  gap: var(--spacing-sm);
  
  span {
    width: 6px;
    height: 6px;
    background: var(--color-secondary-light);
    border-radius: 50%;
    opacity: 0.3;
  }
`;

const LineBreak = styled.div`
  width: 100%;
  max-width: 200px;
  height: 1px;
  background: linear-gradient(90deg, 
    transparent 0%, 
    var(--color-secondary-light) 20%, 
    var(--color-secondary-light) 80%, 
    transparent 100%
  );
`;

const WaveBreak = styled.div`
  width: 100%;
  max-width: 300px;
  height: 20px;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 50%;
    left: -100%;
    width: 200%;
    height: 2px;
    background: var(--color-secondary-light);
    transform: translateY(-50%);
    animation: wave 3s ease-in-out infinite;
  }
  
  @keyframes wave {
    0% {
      left: -100%;
    }
    100% {
      left: 100%;
    }
  }
`;

const GradientBreak = styled.div`
  width: 100%;
  max-width: 400px;
  height: 2px;
  background: linear-gradient(90deg,
    transparent 0%,
    var(--color-accent-light) 25%,
    var(--color-accent-primary) 50%,
    var(--color-accent-light) 75%,
    transparent 100%
  );
  opacity: 0.3;
  border-radius: 2px;
`;

const ContentBreak: React.FC<ContentBreakProps> = ({ 
  variant = 'dots',
  spacing = 'medium' 
}) => {
  const renderBreak = () => {
    switch(variant) {
      case 'line':
        return <LineBreak />;
      case 'wave':
        return <WaveBreak />;
      case 'gradient':
        return <GradientBreak />;
      case 'dots':
      default:
        return (
          <DotsBreak>
            <span />
            <span />
            <span />
          </DotsBreak>
        );
    }
  };
  
  return (
    <BreakContainer $spacing={spacing}>
      {renderBreak()}
    </BreakContainer>
  );
};

export default ContentBreak;